import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTrustNGOSession } from "@/hooks/useTrustNGOSession";
import { hasSigmacertAdminAccess, isPlatformSuperAdminEmail } from "@/lib/trustngo-auth";

interface PostLoginRouterProps {
  children?: React.ReactNode;
}

/**
 * PostLoginRouter - Handles role-based routing after authentication
 *
 * Day 5 Design: Role-based Post-Login Routing
 * - Admin users (role === 'admin') → /admin dashboard
 * - Client/Consulting users → /workspace dashboard
 * - Preserves /workspace as canonical fallback
 */
export default function PostLoginRouter({ children }: PostLoginRouterProps) {
  const { user, session, loading } = useTrustNGOSession();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;

    // Only redirect if we have a valid session and user
    if (!user || !session) return;

    // Check if user has sigmacert tenant access
    const tenants = session.tenants || [];
    if (!tenants.includes('sigmacert') && !isPlatformSuperAdminEmail(user.email)) {
      // No sigmacert access, stay on current page or redirect to home
      return;
    }

    // Role-based routing logic. Keep public pages public; only route users
    // away from the dedicated login screen after a valid session exists.
    if (hasSigmacertAdminAccess(session)) {
      // Admin users go to admin dashboard
      if (window.location.pathname === '/admin/login') {
        navigate('/admin', { replace: true });
      }
    } else {
      // All other authenticated users with sigmacert access go to workspace
      if (window.location.pathname === '/admin/login') {
        navigate('/workspace', { replace: true });
      }
    }
  }, [user, session, loading, navigate]);

  // Show loading while determining routing
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  // Render children if no redirect needed
  return <>{children}</>;
}
